"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildBoroughDeliveryEstimateCacheKey,
  clampSpendToSlider,
  createUnavailableBoroughDeliveryEstimate,
} from "@/lib/borough-delivery-estimator";
import type {
  BoroughDeliveryEstimate,
  BoroughDeliveryEstimateOption,
  MediaAsset,
} from "@/lib/storefront-types";
import { pushDataLayerEvent } from "@/lib/analytics";

type BoroughDeliveryQuoteContext = {
  addressLine1: string;
  postcode: string;
};

type BoroughDeliveryEstimatorProps = {
  boroughName: string;
  boroughLogo?: MediaAsset | null;
  deliveryQuoteContext?: BoroughDeliveryQuoteContext | null;
  initialEstimate: BoroughDeliveryEstimate;
  pagePath: string;
};

/**
 * Display order for the delivery options, keyed by the CMS option `key`.
 * Special Day Delivery leads (it's the highlighted "lowest fee" row), Delivery
 * on Demand follows. Any unknown keys keep their original order after these.
 */
const DELIVERY_OPTION_DISPLAY_ORDER: Record<string, number> = {
  "special-day": 0,
  instant: 1,
};

const CARD_BACKGROUND =
  "linear-gradient(135deg, #001500 0%, #002700 52%, #001500 100%)";
const SPOTLIGHT_BACKGROUND =
  "radial-gradient(ellipse 65% 55% at 78% 18%, rgba(0,150,57,0.30), transparent 68%)";
const SLIDER_FILL_BACKGROUND = "linear-gradient(90deg, #0a6b2e, #1fb053)";

function orderDeliveryOptions(options: BoroughDeliveryEstimate["options"]) {
  return [...options].sort(
    (a, b) =>
      (DELIVERY_OPTION_DISPLAY_ORDER[a.key] ?? Number.MAX_SAFE_INTEGER) -
      (DELIVERY_OPTION_DISPLAY_ORDER[b.key] ?? Number.MAX_SAFE_INTEGER),
  );
}

function formatCurrency(amount: number, currency: string) {
  if (amount <= 0) {
    return "Free";
  }

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatWholeCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * "Sunday Delivery" -> "Sunday", "Special Day Delivery" -> "Special Day".
 * Used to phrase the free-delivery savings nudge from the CMS option name.
 */
function deriveDeliveryDayLabel(optionName: string) {
  const trimmed = optionName.trim();
  const withoutSuffix = trimmed.replace(/\s*delivery$/i, "").trim();
  return withoutSuffix || trimmed || "Sunday";
}

function getInitialSpend(estimate: BoroughDeliveryEstimate) {
  const baseSpend = Number.isFinite(estimate.spend)
    ? estimate.spend
    : estimate.slider.defaultValue;

  return clampSpendToSlider(baseSpend, estimate.slider);
}

export function BoroughDeliveryEstimator({
  boroughName,
  boroughLogo,
  deliveryQuoteContext,
  initialEstimate,
  pagePath,
}: BoroughDeliveryEstimatorProps) {
  const [estimate, setEstimate] = useState(initialEstimate);
  const [spend, setSpend] = useState(() => getInitialSpend(initialEstimate));
  const [isLoading, setIsLoading] = useState(false);
  const cacheRef = useRef<Map<string, BoroughDeliveryEstimate>>(
    new Map([
      [
        buildBoroughDeliveryEstimateCacheKey(
          initialEstimate.path,
          getInitialSpend(initialEstimate),
        ),
        initialEstimate,
      ],
    ]),
  );

  useEffect(() => {
    pushDataLayerEvent("view_borough_page", {});
  }, []);

  useEffect(() => {
    const normalizedSpend = clampSpendToSlider(spend, estimate.slider);
    if (normalizedSpend !== spend) {
      setSpend(normalizedSpend);
    }
  }, [estimate.slider, spend]);

  useEffect(() => {
    const normalizedSpend = clampSpendToSlider(spend, estimate.slider);
    if (normalizedSpend !== spend) {
      return;
    }

    const cacheKey = buildBoroughDeliveryEstimateCacheKey(pagePath, normalizedSpend);
    const cachedEstimate = cacheRef.current.get(cacheKey);
    if (cachedEstimate) {
      if (cachedEstimate !== estimate) {
        setEstimate(cachedEstimate);
      }
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setIsLoading(true);

      try {
        const response = await fetch(
          `/api/borough-delivery-estimate?${new URLSearchParams({
            path: pagePath,
            spend: normalizedSpend.toString(),
          }).toString()}`,
          {
            cache: "no-store",
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Estimator request failed with ${response.status}`);
        }

        const payload = (await response.json()) as BoroughDeliveryEstimate;
        cacheRef.current.set(cacheKey, payload);
        setEstimate(payload);
      } catch {
        if (controller.signal.aborted) {
          return;
        }

        const fallbackEstimate = createUnavailableBoroughDeliveryEstimate(
          pagePath,
          "Delivery estimates are not available for this area right now.",
          normalizedSpend,
        );
        cacheRef.current.set(cacheKey, fallbackEstimate);
        setEstimate(fallbackEstimate);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, 180);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [estimate, pagePath, spend]);

  const currency = estimate.currency;
  const sliderMin = estimate.slider.min;
  const sliderMax = estimate.slider.max;
  const sliderRange = Math.max(sliderMax - sliderMin, 0);
  const isSliderDisabled = sliderRange <= 0;
  const progress =
    sliderRange > 0 ? ((spend - sliderMin) / sliderRange) * 100 : 0;
  const progressStyle = `${progress}%`;

  const orderedOptions = orderDeliveryOptions(estimate.options);
  const primaryOption: BoroughDeliveryEstimateOption | null =
    orderedOptions[0] ?? null;
  const hasOptions = orderedOptions.length > 0;
  const isPrimarySpecialDay = primaryOption?.key === "special-day";

  const specialDayOption =
    orderedOptions.find((option) => option.key === "special-day") ?? null;
  const specialDayLabel = specialDayOption
    ? deriveDeliveryDayLabel(specialDayOption.name)
    : null;
  // The free-delivery threshold is a borough-static value, so read it from the
  // SSR `initialEstimate` (and hold it stable) rather than the per-spend
  // estimate that gets replaced on every slider re-fetch — otherwise the
  // marker would flicker/vanish as the customer drags.
  const freeThreshold = useMemo(
    () =>
      initialEstimate.options.find((option) => option.key === "special-day")
        ?.freeDeliveryThreshold ?? null,
    [initialEstimate],
  );
  const isSpecialDayFree = specialDayOption
    ? specialDayOption.price <= 0
    : false;
  const isFreeUnlocked =
    freeThreshold != null ? spend >= freeThreshold : isSpecialDayFree;

  // The free-threshold tick only renders when we know the exact threshold and
  // it falls strictly inside the slider range.
  const tickPercent =
    freeThreshold != null &&
    sliderRange > 0 &&
    freeThreshold > sliderMin &&
    freeThreshold < sliderMax
      ? ((freeThreshold - sliderMin) / sliderRange) * 100
      : null;

  let savingsText: string | null = null;
  let savingsIsUnlocked = false;
  if (specialDayOption && specialDayLabel) {
    if (isFreeUnlocked) {
      savingsText = `Free ${specialDayLabel} delivery unlocked`;
      savingsIsUnlocked = true;
    } else if (freeThreshold != null) {
      const amountToFree = Math.max(0, freeThreshold - spend);
      savingsText = `Spend ${formatWholeCurrency(amountToFree, currency)} more for free ${specialDayLabel} delivery`;
    } else {
      savingsText = `Order more to unlock free ${specialDayLabel} delivery`;
    }
  }

  return (
    <section className="storefront-surface-dark overflow-hidden text-white">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
        <div
          className="relative mx-auto w-full max-w-[480px] overflow-hidden rounded-[32px] p-6 text-white shadow-[0_34px_80px_-46px_rgba(0,0,0,0.6)] sm:p-[30px] lg:max-w-5xl lg:p-10"
          style={{ background: CARD_BACKGROUND }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{ background: SPOTLIGHT_BACKGROUND }}
          />
          <div className="relative">
            {deliveryQuoteContext ? (
              <div className="mb-5 border-b border-white/15 pb-4">
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.26em] text-white/60">
                  Delivery options for
                </p>
                <p className="mt-1 text-base font-semibold leading-snug text-white">
                  {deliveryQuoteContext.addressLine1.trim()}{" "}
                  {deliveryQuoteContext.postcode.trim()}
                </p>
              </div>
            ) : null}

            {/* Header — borough logo sits top-right, leaving the title room to breathe */}
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <span className="block text-[11px] font-semibold uppercase tracking-[0.26em] text-white/60">
                  Delivery calculator
                </span>
                <h1>
                  {/* Trailing {" "} keeps the H1's accessible text reading
                      "Nigerian Food Delivery to {borough}" — trailing white-
                      space at the end of a block line never renders, so the
                      visible text is unchanged. */}
                  <span className="mt-1.5 block text-[18px] font-semibold leading-[1.2] text-white/80 lg:text-[20px]">
                    Nigerian Food Delivery to{" "}
                  </span>
                  <span className="block text-[26px] font-bold leading-[1.1] tracking-[-0.015em] text-white lg:text-[36px]">
                    {boroughName}
                  </span>
                </h1>
              </div>
              {boroughLogo ? (
                <div className="relative flex h-[58px] w-[58px] flex-none items-center justify-center overflow-hidden rounded-[16px] bg-white/90 lg:h-[68px] lg:w-[68px]">
                  <Image
                    src={boroughLogo.url}
                    alt={boroughLogo.alt || `${boroughName} logo`}
                    fill
                    sizes="(min-width: 1024px) 68px, 58px"
                    className="object-contain p-1.5"
                  />
                </div>
              ) : null}
            </div>

            {/* SEO + intent-setting lead copy */}
            <p className="mt-4 max-w-2xl text-[13px] leading-relaxed text-white/65 lg:text-sm">
              See live delivery prices for authentic Nigerian food in {boroughName},
              London. Order on demand for same-day delivery to your door
              {specialDayLabel
                ? `, or book a scheduled ${specialDayLabel} run for a lower fee`
                : ""}{" "}
              — collection from our kitchen is always free.
            </p>

            {/* Spend value + slider */}
            <div className="mt-6">
              <span className="text-[12.5px] font-semibold text-white/[0.66]">
                When you spend{" "}
                <span className="text-white">
                  {formatWholeCurrency(spend, currency)}
                </span>
              </span>

              <div className="relative mt-[52px] h-[30px]">
                <input
                  id="borough-delivery-estimate-slider"
                  type="range"
                  aria-label="Planned spend"
                  min={sliderMin}
                  max={sliderMax}
                  step={estimate.slider.step}
                  value={spend}
                  disabled={isSliderDisabled}
                  onChange={(event) => {
                    setSpend(Number(event.target.value));
                  }}
                  className="borough-calc-range"
                />
                <div className="borough-calc-rail pointer-events-none absolute inset-y-0 left-[14px] right-[14px]">
                  <div className="absolute left-0 right-0 top-1/2 h-[12px] -translate-y-1/2 rounded-full bg-white/[0.16]" />
                  <div
                    className="absolute left-0 top-1/2 h-[12px] -translate-y-1/2 rounded-full"
                    style={{
                      width: progressStyle,
                      background: SLIDER_FILL_BACKGROUND,
                    }}
                  />
                  {tickPercent != null ? (
                    <>
                      {/* Free-delivery marker: where the scheduled run becomes free */}
                      <div
                        className="absolute top-1/2 h-[26px] w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#f0b53d] shadow-[0_0_8px_rgba(240,181,61,0.65)]"
                        style={{ left: `${tickPercent}%` }}
                      />
                      {specialDayLabel ? (
                        <span
                          className="absolute top-full mt-1 -translate-x-1/2 whitespace-nowrap text-[9px] font-bold uppercase tracking-[0.08em] text-[#f0b53d]"
                          style={{ left: `${tickPercent}%` }}
                        >
                          Free {specialDayLabel}
                        </span>
                      ) : null}
                    </>
                  ) : null}
                  <div
                    className="borough-calc-thumb absolute top-1/2 h-[28px] w-[28px] -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-[#eafaef] bg-[#1fb053] shadow-[0_8px_18px_-8px_rgba(0,0,0,0.75)]"
                    style={{ left: progressStyle }}
                  />
                  <div
                    className="absolute bottom-full mb-4 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#f0b53d] px-[14px] py-2 text-[18px] font-bold leading-none text-[#102313] shadow-[0_14px_26px_-14px_rgba(0,0,0,0.7)] after:absolute after:left-1/2 after:top-full after:-mt-1 after:h-[9px] after:w-[9px] after:-translate-x-1/2 after:rotate-45 after:bg-[#f0b53d] after:content-['']"
                    style={{ left: progressStyle }}
                  >
                    {formatWholeCurrency(spend, currency)}
                  </div>
                </div>
              </div>

              <div className="mt-[18px] flex justify-between text-[11px] font-semibold text-white/50">
                <span>{formatWholeCurrency(sliderMin, currency)}</span>
                <span>{formatWholeCurrency(sliderMax, currency)}</span>
              </div>

              {savingsText ? (
                <div className="mt-[13px] flex items-center gap-[9px]">
                  <span
                    aria-hidden="true"
                    className={`h-[7px] w-[7px] flex-none rounded-full ${savingsIsUnlocked ? "bg-[#9cd4a0]" : "bg-[#f0b53d]"}`}
                  />
                  <span
                    className={`text-[13px] font-bold ${savingsIsUnlocked ? "text-[#9cd4a0]" : "text-[#f0b53d]"}`}
                  >
                    {savingsText}
                  </span>
                </div>
              ) : null}
            </div>

            <div className="mt-2 min-h-5" aria-live="polite">
              {isLoading ? (
                <p className="text-[12px] font-medium text-white/55">
                  Updating delivery estimate…
                </p>
              ) : !hasOptions && estimate.message ? (
                <p className="text-[12px] font-medium text-[#f0b53d]">
                  {estimate.message}
                </p>
              ) : null}
            </div>

            {/* Comparison panel */}
            {hasOptions ? (
              <div className="mt-[18px] overflow-hidden rounded-[22px] border border-white/12">
                {orderedOptions.map((option, index) => {
                  const isPrimary = index === 0;
                  const isFree = option.price <= 0;
                  const showLowestFeeTag = isPrimary && isPrimarySpecialDay;
                  return (
                    <div key={option.key}>
                      {index > 0 ? (
                        <div className="h-px bg-white/10" />
                      ) : null}
                      <div
                        className={`flex items-center justify-between gap-[14px] px-[18px] py-[16px] ${
                          isPrimary && isPrimarySpecialDay
                            ? "border-l-[3px] border-[#f0b53d] bg-[rgba(240,181,61,0.09)]"
                            : "bg-white/[0.04]"
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-[9px]">
                            <span className="text-[17px] font-bold text-white">
                              {option.name}
                            </span>
                            {showLowestFeeTag ? (
                              <span className="rounded-full bg-[#f0b53d] px-2 py-[3px] text-[9px] font-extrabold uppercase tracking-[0.12em] text-[#102313]">
                                Lowest fee
                              </span>
                            ) : null}
                          </div>
                          <span className="mt-1 block text-[12px] text-white/60">
                            {option.description}
                          </span>
                        </div>
                        <span
                          className={`text-[26px] font-bold leading-none tracking-[-0.03em] lg:text-[30px] ${
                            isFree ? "text-[#f0b53d]" : "text-white"
                          }`}
                        >
                          {formatCurrency(option.price, currency)}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {/* Click & Collect — always free, regardless of basket size */}
                <div className="h-px bg-white/10" />
                <div className="flex items-center justify-between gap-[14px] bg-white/[0.04] px-[18px] py-[16px]">
                  <div className="min-w-0">
                    <span className="text-[17px] font-bold text-white">
                      Click &amp; Collect
                    </span>
                    <span className="mt-1 block text-[12px] text-white/60">
                      Always Free
                    </span>
                  </div>
                  <span className="text-[26px] font-bold leading-none tracking-[-0.03em] text-[#f0b53d] lg:text-[30px]">
                    {formatCurrency(0, currency)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="mt-[18px] overflow-hidden rounded-[22px] border border-white/12 bg-white/[0.04] px-[18px] py-[16px]">
                <p className="text-[17px] font-bold text-white">
                  Delivery prices are temporarily unavailable.
                </p>
                <p className="mt-2 text-[12px] text-white/60">
                  You can still open the menu and begin your order while we fetch
                  the latest borough pricing.
                </p>
              </div>
            )}

            {/* CTA */}
            <Link
              href="/menu"
              className="mt-6 flex w-full items-center justify-center rounded-full bg-[#009639] px-4 py-4 text-[15px] font-bold text-white shadow-[0_20px_40px_-22px_rgba(0,39,0,0.75)] transition-[background-color,color,box-shadow] duration-[180ms] ease-in-out hover:bg-white hover:text-[#009639] hover:shadow-[inset_0_0_0_2px_#009639] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#009639] motion-reduce:transition-none"
            >
              Order Now
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
